/* YoloLife — header predictive search
 * Dawn-pattern custom element + localStorage recent searches + dim backdrop.
 * - Suggestions: Shopify Predictive Search API (/search/suggest, section render).
 *   Unsupported for zh buyer locale (official) → fetch yields no results, we fall
 *   back to a "view all" link; recent searches still work in every language.
 * - No external deps; pure DOM. Pairs with sections/predictive-search.liquid.
 * - SKU fragments ("7591", "591"): a "Matching SKUs" group from assets/yl-sku-search.js is added on
 *   top, because Shopify search only finds whole SKUs and prefixes (tested 2026-11-02).
 */
(function () {
  'use strict';

  var HISTORY_KEY = 'yl-search-history';
  var HISTORY_MAX = 6;
  var DEBOUNCE_MS = 300;
  // Per-instance counter — the desktop header and the mobile-menu form each mount a
  // YlPredictiveSearch instance; option ids must be unique per instance so they don't
  // collide in the DOM (and so each input's aria-activedescendant points at its own option).
  var instanceSeq = 0;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  class YlPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this._uid = 'yl-ps-' + (instanceSeq++);
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-yl-search-panel]');
      // The dim backdrop is shared (one per page); instances inside an existing overlay
      // (the mobile menu) opt out via data-no-backdrop so it can't cover that overlay.
      this.backdrop = 'noBackdrop' in this.dataset
        ? null
        : document.querySelector('[data-yl-search-backdrop]');
      this.clearBtn = this.querySelector('.yl-header__search-clear');
      // Persistent SR live region (WCAG 4.1.3): exists in DOM up-front, never replaced.
      this.status = this.querySelector('[data-yl-search-status]');
      this.i18n = {
        recent: this.dataset.i18nRecent || 'Recent searches',
        clear: this.dataset.i18nClear || 'Clear',
        viewAll: this.dataset.i18nViewAll || 'View all results for',
        results: this.dataset.i18nResults || '%count% results found',
        noResults: this.dataset.i18nNoResults || 'No matches.'
      };
      this._timer = null;
      this._requestId = 0;
      this._controller = null;
      // ARIA combobox virtual focus: DOM focus stays on the input, the active
      // listbox option is tracked via aria-activedescendant (WAI-ARIA APG).
      this.options = [];
      this.activeIndex = -1;
      this._activeEl = null;
      this._onInput = this.onInput.bind(this);
      this._onFocus = this.onFocus.bind(this);
      this._onFocusOut = this.onFocusOut.bind(this);
      this._onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.input || !this.panel) return;
      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('focus', this._onFocus);
      this.addEventListener('focusout', this._onFocusOut);
      document.addEventListener('keydown', this._onKeydown);
      if (this.form) this.form.addEventListener('submit', () => this.saveTerm(this.input.value));
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());
      // Save the term when a suggestion / "view all" link is followed.
      this.panel.addEventListener('mousedown', (e) => {
        if (e.target.closest('a')) this.saveTerm(this.input.value);
      });
      // Native reset (✕) clears the field — refocus and fall back to history.
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          window.setTimeout(() => { this.input.focus(); this.onInput(); }, 0);
        });
      }
    }

    disconnectedCallback() {
      this.cancelSuggestions();
      if (this.input) {
        this.input.removeEventListener('input', this._onInput);
        this.input.removeEventListener('focus', this._onFocus);
      }
      this.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('keydown', this._onKeydown);
    }

    onFocus() {
      if (this.input.value.trim().length) this.onInput();
      else this.renderHistory();
    }

    onInput() {
      var term = this.input.value.trim();
      this.close();
      if (!term.length) { this.renderHistory(); return; }
      this._timer = window.setTimeout(() => this.fetchSuggestions(term), DEBOUNCE_MS);
    }

    onKeydown(e) {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        this.close();
        this.input.blur();
        return;
      }
      // Arrow / Enter only act while the search field holds DOM focus (options
      // are virtual — input keeps focus, aria-activedescendant points the option).
      if (document.activeElement !== this.input) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.move(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.move(-1);
      } else if (e.key === 'Enter') {
        if (this.activeIndex >= 0 && this._activeEl) {
          // Follow the active suggestion instead of submitting the whole-term search.
          var anchor = this.optionAnchor(this._activeEl);
          if (anchor && anchor.href) {
            e.preventDefault();
            this.saveTerm(this.input.value);
            window.location.assign(anchor.href);
          }
        } else {
          if (!this.input.value.trim()) return;
          e.preventDefault();
          this.saveTerm(this.input.value);
          window.location.assign(this.searchHref(this.input.value));
        }
      }
      // Home/End are left to the textbox (APG combobox semantics: move text cursor).
    }

    // The clickable <a> for an option element (option may BE the anchor — viewall —
    // or wrap one — a <li role="option">).
    optionAnchor(el) {
      return el && el.matches('a') ? el : el && el.querySelector('a');
    }

    // Collect every navigable option in the freshly-rendered panel and give each a
    // stable id + role/aria-selected so aria-activedescendant can reference it.
    // Covers all three render paths: server suggestions, recent history, view-all.
    refreshOptions() {
      this.options = Array.prototype.slice.call(
        this.panel.querySelectorAll('li.yl-psearch__opt, a.yl-psearch__viewall')
      );
      this.options.forEach((el, i) => {
        el.setAttribute('role', 'option');
        el.id = this._uid + '-opt-' + i;
        el.setAttribute('aria-selected', 'false');
      });
      this.setActive(-1);
    }

    // Move virtual focus by delta; no wrap — overshooting a boundary returns focus
    // to the combobox (index -1), per the APG editable-combobox spec.
    move(delta) {
      if (!this.options.length) return;
      var next;
      if (this.activeIndex === -1) {
        next = delta > 0 ? 0 : this.options.length - 1;
      } else {
        next = this.activeIndex + delta;
        if (next < 0 || next >= this.options.length) next = -1;
      }
      this.setActive(next);
    }

    setActive(index) {
      if (this._activeEl) this._activeEl.setAttribute('aria-selected', 'false');
      this.activeIndex = index;
      if (index < 0 || index >= this.options.length) {
        this._activeEl = null;
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      var el = this.options[index];
      this._activeEl = el;
      el.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    onFocusOut(e) {
      // close only when focus truly leaves the component (not when moving to a result)
      if (!this.contains(e.relatedTarget)) this.close();
    }

    // Update the persistent live region so screen readers hear the result count
    // (WCAG 4.1.3). count>0 → "N results found"; 0 → the localized no-matches line.
    announce(count) {
      if (!this.status) return;
      this.status.textContent = count > 0
        ? this.i18n.results.replace('%count%', String(count))
        : this.i18n.noResults;
    }

    cancelSuggestions() {
      window.clearTimeout(this._timer);
      this._requestId += 1;
      if (this._controller) this._controller.abort();
      this._controller = null;
    }

    fetchSuggestions(term) {
      var requestId = this._requestId;
      this._controller = new AbortController();
      var isCurrent = () => requestId === this._requestId &&
        this.isConnected && this.input.value.trim() === term;
      var url = this.searchHref(term) + '&section_id=yl-search-suggestions';
      // SKU fragments ("7591"): start the SKU index download alongside the section request, so the
      // first lookup is not two round trips in a row. Failures stay silent (see renderSkuMatches).
      var skuApi = this.skuApi(term);
      if (skuApi && !skuApi.peek()) skuApi.load().catch(function () {});
      fetch(url, { signal: this._controller.signal })
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          if (!isCurrent()) return;
          var section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-yl-search-suggestions');
          var markup = section ? section.innerHTML.trim() : '';
          if (markup) {
            this.panel.innerHTML = markup;
            this.readSkuText();
            // Cached SKU index → the SKU group is in place before open() collects the options.
            this.renderSkuMatches(term, isCurrent);
            this.open();
            // Announce the result count to AT (WCAG 4.1.3). Count actual suggestion
            // rows (SKU matches + products + collections); 0 → the "no matches" message.
            // The always-present view-all anchor is not a suggestion, so it is excluded.
            this.announce(this.panel.querySelectorAll('li.yl-psearch__opt').length);
          } else { this.renderFallback(term, isCurrent); }
        })
        .catch((error) => {
          if (isCurrent() && error.name !== 'AbortError') this.renderFallback(term, isCurrent);
        });
    }

    // ---- SKU fragment matches (assets/yl-sku-search.js) ----
    // Shopify search finds whole SKUs and prefixes only ("7591" → 0 results, tested on yololife.ae
    // 2026-11-02), so SKU-like queries get a "Matching SKUs" group on top of the dropdown.
    skuApi(term) {
      var api = window.YoloLifeSkuSearch;
      return api && api.load && api.isSkuQuery(term) ? api : null;
    }

    // Translated labels ride along in the section response (sections/yl-search-suggestions.liquid);
    // kept on the instance so the fallback panel can still label SKU rows.
    readSkuText() {
      var config = this.panel.querySelector('[data-yl-sku-config]');
      if (!config) return;
      this._skuText = {
        heading: config.getAttribute('data-heading') || '',
        label: config.getAttribute('data-label') || 'SKU',
        loading: config.getAttribute('data-loading') || '',
        soldOut: config.getAttribute('data-sold-out') || ''
      };
      config.remove();
    }

    renderSkuMatches(term, isCurrent) {
      var api = this.skuApi(term);
      if (!api || !this._skuText) return;
      var items = api.peek();
      if (items) { this.insertSkuGroup(term, items, false); return; }
      // First lookup: a plain loading line (not an option, so arrow keys skip it).
      this.panel.insertAdjacentHTML('afterbegin',
        '<p class="yl-psearch__sku-loading" data-yl-sku-group>' + escapeHtml(this._skuText.loading) + '</p>');
      api.load().then(
        (loaded) => { if (isCurrent()) this.insertSkuGroup(term, loaded, true); },
        () => { if (isCurrent()) this.insertSkuGroup(term, [], true); }
      );
    }

    // live = the panel is already open: rebuild the option list, keep the keyboard selection on the
    // same row, and re-announce the count when SKU rows were added.
    insertSkuGroup(term, items, live) {
      var api = window.YoloLifeSkuSearch;
      this.panel.querySelectorAll('[data-yl-sku-group]').forEach((el) => el.remove());
      var shown = Array.prototype.map.call(
        this.panel.querySelectorAll('[data-product-id]'),
        (el) => el.getAttribute('data-product-id')
      );
      var matches = api.matchSkus(items, term, { excludeProductIds: shown });
      if (matches.length) {
        this.panel.insertAdjacentHTML('afterbegin', this.skuGroupHtml(term, matches));
        // "No matches yet" would contradict the SKU rows right above it.
        var empty = this.panel.querySelector('.yl-psearch__empty');
        if (empty) empty.remove();
      }
      if (!live) return;
      var keep = this._activeEl;
      this.refreshOptions();
      var index = keep ? this.options.indexOf(keep) : -1;
      if (index >= 0) this.setActive(index);
      if (matches.length) this.announce(this.panel.querySelectorAll('li.yl-psearch__opt').length);
    }

    skuGroupHtml(term, matches) {
      var api = window.YoloLifeSkuSearch;
      var text = this._skuText;
      var headId = this._uid + '-sku-head';
      var rows = matches.map((item) => {
        var href = api.safeUrl(item.url);
        if (!href) return '';
        var image = api.safeUrl(item.image);
        var thumb = image
          ? '<span class="yl-psearch__thumb"><img class="yl-psearch__thumb-img" src="' + escapeHtml(image) +
            '" alt="" width="44" height="44" loading="lazy" decoding="async"></span>'
          : '<span class="yl-psearch__thumb yl-psearch__thumb--empty" aria-hidden="true"></span>';
        var soldOut = item.available === false && text.soldOut
          ? ' <span class="yl-psearch__sku-soldout">' + escapeHtml(text.soldOut) + '</span>'
          : '';
        return '<li class="yl-psearch__opt" role="option" aria-selected="false">' +
          '<a href="' + escapeHtml(href) + '" class="yl-psearch__row yl-psearch__product" tabindex="-1">' + thumb +
          '<span class="yl-psearch__product-info">' +
          '<span class="yl-psearch__product-title">' + escapeHtml(item.title) + '</span>' +
          '<span class="yl-psearch__sku">' + escapeHtml(text.label) + ' ' + api.highlightSku(item.sku, term) + '</span>' +
          '<span class="yl-psearch__product-price">' + escapeHtml(item.price) + soldOut + '</span>' +
          '</span></a></li>';
      }).join('');
      if (!rows) return '';
      return '<div class="yl-psearch__group yl-psearch__group--sku" role="group" aria-labelledby="' + headId +
        '" data-yl-sku-group>' +
        '<h2 id="' + headId + '" class="yl-psearch__group-head">' + escapeHtml(text.heading) + '</h2>' +
        '<ul class="yl-psearch__list" role="presentation">' + rows + '</ul></div>';
    }

    // zh locale / API error: no section results — still let the user reach the results page.
    searchHref(term) {
      return window.YoloLifeSearch.href(term, root());
    }

    renderFallback(term, isCurrent) {
      var href = this.searchHref(term);
      this.panel.innerHTML =
        '<a href="' + href + '" class="yl-psearch__viewall">' +
        escapeHtml(this.i18n.viewAll) + ' <span class="yl-psearch__viewall-term">“' +
        escapeHtml(term) + '”</span></a>';
      if (isCurrent) this.renderSkuMatches(term, isCurrent);
      this.open();
      var skuRows = this.panel.querySelectorAll('li.yl-psearch__opt').length;
      if (skuRows) this.announce(skuRows);
    }

    renderHistory() {
      var items = this.getHistory();
      if (!items.length) { this.close(); return; }
      var rows = items.map((t) => {
        var href = this.searchHref(t);
        return '<li class="yl-psearch__opt" role="option" aria-selected="false"><a href="' + href +
          '" class="yl-psearch__row yl-psearch__row--recent" tabindex="-1">' +
          '<svg class="yl-psearch__recent-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
          '<circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.3"/>' +
          '<path d="M8 4.8V8l2 1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>' +
          '<span>' + escapeHtml(t) + '</span></a></li>';
      }).join('');
      this.panel.innerHTML =
        '<div class="yl-psearch__group" role="group" aria-label="' + escapeHtml(this.i18n.recent) + '">' +
        '<div class="yl-psearch__group-head yl-psearch__group-head--row">' +
        '<span>' + escapeHtml(this.i18n.recent) + '</span>' +
        '<button type="button" class="yl-psearch__clear-recent" data-yl-clear-history>' +
        escapeHtml(this.i18n.clear) + '</button></div>' +
        '<ul class="yl-psearch__list" role="presentation">' + rows + '</ul></div>';
      var clear = this.panel.querySelector('[data-yl-clear-history]');
      if (clear) {
        clear.addEventListener('click', () => {
          this.clearHistory();
          this.close();
          this.input.focus();
        });
      }
      this.open();
    }

    getHistory() {
      try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; }
      catch (e) { return []; }
    }
    saveTerm(term) {
      term = (term || '').trim();
      if (!term) return;
      var lower = term.toLowerCase();
      var list = this.getHistory().filter((x) => x.toLowerCase() !== lower);
      list.unshift(term);
      list = list.slice(0, HISTORY_MAX);
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch (e) {}
    }
    clearHistory() {
      try { localStorage.removeItem(HISTORY_KEY); } catch (e) {}
    }

    open() {
      if (!this.panel.innerHTML.trim()) return;
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.refreshOptions();
      if (this.backdrop) this.backdrop.hidden = false;
    }
    close() {
      this.cancelSuggestions();
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this._activeEl = null;
      // Clear the announcement so reopening a stale count is not re-read.
      if (this.status) this.status.textContent = '';
      if (this.backdrop) this.backdrop.hidden = true;
    }
  }

  if (!customElements.get('yl-predictive-search')) {
    customElements.define('yl-predictive-search', YlPredictiveSearch);
  }
})();
